import * as yup from 'yup'
import { isRichTextEmpty } from 'components/articleEditor/helpers'

// Create and edit send the same editable fields, so they share one schema.
export const articleSchema = yup.object({
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .min(3, 'Title must be at least 3 characters')
        .max(255, 'Title cannot exceed 255 characters'),

    // Optional: the backend derives the slug from the title when omitted.
    slug: yup
        .string()
        .trim()
        .nullable()
        .max(255, 'Custom URL cannot exceed 255 characters')
        .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
            message: 'Use lowercase letters, numbers and single hyphens only',
            excludeEmptyString: true,
        }),

    short_description: yup
        .string()
        .trim()
        .required('Short description is required')
        .max(500, 'Short description cannot exceed 500 characters'),

    author: yup
        .string()
        .trim()
        .required('Author is required')
        .min(2, 'Author must be at least 2 characters')
        .max(100, 'Author cannot exceed 100 characters'),

    content_html: yup
        .string()
        .nullable()
        .test('content-required', 'Article content is required', (value) => !isRichTextEmpty(value)),

    seoTitle: yup.string().trim().max(255, 'SEO title cannot exceed 255 characters'),
    metaDescription: yup.string().trim().max(500, 'Meta description cannot exceed 500 characters'),
    focusKeyword: yup.string().trim().max(255, 'Focus keyword cannot exceed 255 characters'),
    searchIntent: yup.string().trim().max(500, 'Search intent cannot exceed 500 characters'),
    directAnswer: yup.string().trim().max(10000, 'Direct answer cannot exceed 10,000 characters'),
    aiSummary: yup.string().trim().max(20000, 'AI summary cannot exceed 20,000 characters'),
    contentType: yup
        .string()
        .oneOf(['Article', 'BlogPosting', 'NewsArticle'], 'Choose a content type'),
    keyTakeaways: yup
        .array()
        .of(yup.string().trim().max(500, 'Each takeaway cannot exceed 500 characters'))
        .max(5, 'Add no more than 5 takeaways')
        .test('takeaway-count', 'Add 3 to 5 takeaways, or leave them all empty', (items) => {
            const filled = (items || []).filter((item) => item?.trim()).length
            return filled === 0 || (filled >= 3 && filled <= 5)
        }),
    faqs: yup
        .array()
        .max(20, 'Add no more than 20 questions')
        .of(
            yup.object({
                question: yup.string().trim().max(500, 'Question cannot exceed 500 characters'),
                answer: yup.string().trim().max(5000, 'Answer cannot exceed 5,000 characters'),
            })
        )
        .test('faq-pairs', 'Each question needs an answer', (items) =>
            (items || []).every((item) => {
                const question = item?.question?.trim()
                const answer = item?.answer?.trim()
                return (!question && !answer) || Boolean(question && answer)
            })
        ),
    relatedArticles: yup
        .string()
        .trim()
        .test('related-ids', 'Enter published article IDs separated by commas', (value) => {
            if (!value) return true
            const ids = value.split(',').map((item) => item.trim()).filter(Boolean)
            return ids.length <= 20 && ids.every((id) => /^[1-9]\d*$/.test(id)) && new Set(ids).size === ids.length
        }),
    connectedContent: yup
        .array()
        .max(50, 'Connect no more than 50 items')
        .of(
            yup.object({
                contentType: yup.string().oneOf(['category', 'show', 'episode', 'collection']).required(),
                contentId: yup.string().uuid('Choose a valid item').required(),
                name: yup.string().nullable(),
                thumbnail: yup.string().nullable(),
            })
        ),
})

export type ArticleFormValues = yup.InferType<typeof articleSchema>